import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";
import { CATEGORY_COLOR } from "@/data/site";
import { ArrowRight, ArrowUpRight } from "lucide-react";

export function BlogPreview() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get("/blog").then(({ data }) => setPosts(data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <section id="blog" className="relative py-24 lg:py-32" data-testid="blog-preview">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
            <div>
              <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-orange">Insights</p>
              <h2 className="max-w-2xl font-display text-4xl font-extrabold leading-none tracking-tight sm:text-5xl lg:text-6xl">
                From the growth desk.
              </h2>
            </div>
            <Link to="/blog" data-testid="view-all-blog" className="group inline-flex items-center gap-2 text-sm font-semibold text-white">
              View all articles <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
          {posts.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08}>
              <BlogCard post={p} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function BlogCard({ post }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      data-testid={`blog-card-${post.slug}`}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-white/8 bg-surface transition-colors hover:border-orange/30"
    >
      <div className="relative h-48 overflow-hidden">
        {post.cover_image ? (
          <img src={post.cover_image} alt={post.title} className="h-full w-full object-cover opacity-80 transition-transform duration-700 group-hover:scale-105" />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-indigo to-magenta/40" />
        )}
      </div>
      <div className="flex flex-1 flex-col p-6">
        <span className={`w-fit rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-widest ${CATEGORY_COLOR[post.category] || "text-muted border-white/20"}`}>
          {post.category}
        </span>
        <h3 className="mt-4 font-display text-xl font-bold leading-snug text-white group-hover:text-orange-bright">{post.title}</h3>
        <p className="mt-3 flex-1 text-sm font-light leading-relaxed text-muted line-clamp-3">{post.excerpt}</p>
        <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-white">
          Read <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </span>
      </div>
    </Link>
  );
}
